import { Duration } from "luxon";

import { nanoSecToSecLossy } from "./time";

/**
 * Format a duration in seconds as `Xd Yh Zm Ss Mms`, trimming leading
 * and trailing zero units (e.g. `60.3` → `"1m 0s 300ms"`). Days are
 * uncapped; zero is rendered explicitly as `"0d 0h 0m 0s 0ms"` to
 * distinguish empty from absent. Throws on negative input.
 */
export function formatDuration(durationInSeconds: number) {
  if (durationInSeconds < 0) {
    throw new Error("Duration is negative");
  }

  if (durationInSeconds === 0) {
    return "0d 0h 0m 0s 0ms";
  }

  const dur = Duration.fromObject({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: durationInSeconds,
    milliseconds: 0,
  });

  // Normalize spreads the seconds into the time units
  // explicitly described in the duration object
  const normalized = dur.normalize();

  const timeUnitsTemplateStrings = ["d'd'", "h'h'", "m'm'", "s's'", "S'ms'"];
  const timeUnits = [
    normalized.days,
    normalized.hours,
    normalized.minutes,
    normalized.seconds,
    normalized.milliseconds,
  ];

  const firstNonZeroIndex = timeUnits.findIndex((timeUnit) => timeUnit > 0);

  let lastNonZeroIndex = -1;
  for (let i = timeUnits.length - 1; i >= 0; i--) {
    if (timeUnits[i] > 0) {
      lastNonZeroIndex = i;
      break;
    }
  }

  const templateString = timeUnitsTemplateStrings
    .slice(firstNonZeroIndex, lastNonZeroIndex + 1)
    .join(" ");

  return dur.toFormat(templateString);
}

/**
 * Format a nanosecond duration via {@link formatDuration}. Returns `""`
 * for missing or negative values so table cells stay blank rather than
 * showing a negative span. The ns→seconds step uses
 * {@link nanoSecToSecLossy}; sub-microsecond detail is dropped.
 */
export function formatNanoDuration(value: bigint | null | undefined): string {
  if (value === null || value === undefined) {
    return "";
  }
  if (value < 0n) {
    return "";
  }
  return formatDuration(nanoSecToSecLossy(value));
}
